import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import PatientSuggest from '../PatientSuggest';
import EmployeeSuggest from '../EmployeeSuggest';

const WAIVER_TYPES = [
  { value: 'IO_Agency', label: 'Individual Options (IO) - Agency' },
  { value: 'IO_FMS', label: 'Individual Options (IO) - FMS' },
  { value: 'L1_Agency', label: 'Level One (L1) - Agency' },
  { value: 'L1_FMS', label: 'Level One (L1) - FMS' },
  { value: 'SELF_Agency', label: 'SELF Waiver - Agency' },
];

const OHIO_COUNTIES = [
  'Franklin', 'Cuyahoga', 'Hamilton', 'Summit', 'Montgomery',
  'Lucas', 'Butler', 'Stark', 'Lorain', 'Mahoning',
];

export const DODDClaimModal = ({
  open,
  onOpenChange,
  formData,
  onChange,
  onSubmit,
  saving,
  selectedWaiverCodes,
  patients,
  employees,
  codeInfo,
  estimatedReimbursement,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Create DODD Claim</DialogTitle>
          <DialogDescription>Create a claim for Ohio DODD waiver services</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Patient *</Label>
              <PatientSuggest
                onSelect={(p) => onChange('patient_id', p.id)}
                placeholder="Type patient name or Medicaid ID..."
                value={formData.patient_id ? `${patients.find((p) => p.id === formData.patient_id)?.first_name} ${patients.find((p) => p.id === formData.patient_id)?.last_name}` : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>Employee *</Label>
              <EmployeeSuggest
                onSelect={(e) => onChange('employee_id', e.id)}
                placeholder="Type employee name..."
                value={formData.employee_id ? `${employees.find((e) => e.id === formData.employee_id)?.first_name} ${employees.find((e) => e.id === formData.employee_id)?.last_name}` : ''}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Waiver Type *</Label>
              <Select value={formData.waiver_type} onValueChange={(val) => onChange('waiver_type', val)}>
                <SelectTrigger data-testid="dodd-waiver-select">
                  <SelectValue placeholder="Select waiver" />
                </SelectTrigger>
                <SelectContent>
                  {WAIVER_TYPES.map((w) => (
                    <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service Code *</Label>
              <Select value={formData.service_code} onValueChange={(val) => onChange('service_code', val)} disabled={!selectedWaiverCodes.length}>
                <SelectTrigger data-testid="dodd-code-select">
                  <SelectValue placeholder="Select code" />
                </SelectTrigger>
                <SelectContent>
                  {selectedWaiverCodes.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      <span className="font-mono">{c.code}</span> - {c.description} (${c.rate})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Service Date *</Label>
              <Input type="date" value={formData.service_date} onChange={(e) => onChange('service_date', e.target.value)} required data-testid="dodd-date" />
            </div>
            <div className="space-y-2">
              <Label>Time In *</Label>
              <Input type="time" value={formData.time_in} onChange={(e) => onChange('time_in', e.target.value)} required data-testid="dodd-time-in" />
            </div>
            <div className="space-y-2">
              <Label>Time Out *</Label>
              <Input type="time" value={formData.time_out} onChange={(e) => onChange('time_out', e.target.value)} required data-testid="dodd-time-out" />
            </div>
          </div>

          {codeInfo && (
            <Card className="bg-gradient-to-br from-indigo-50 to-white border-indigo-200">
              <CardContent className="pt-4">
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-slate-500">Code</p>
                    <p className="font-mono font-bold text-indigo-700">{codeInfo.code}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Units</p>
                    <p className="text-xl font-bold">{formData.units}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Rate</p>
                    <p className="font-mono font-bold">${codeInfo.rate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Est. Total</p>
                    <p className="text-xl font-bold text-emerald-600 font-mono">${estimatedReimbursement}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-6">
            <div className="flex items-center gap-2">
              <Checkbox id="is_overtime" checked={formData.is_overtime} onCheckedChange={(checked) => onChange('is_overtime', checked)} />
              <Label htmlFor="is_overtime" className="cursor-pointer">Overtime</Label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="is_longevity" checked={formData.is_longevity} onCheckedChange={(checked) => onChange('is_longevity', checked)} />
              <Label htmlFor="is_longevity" className="cursor-pointer">Longevity Pay</Label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>PAWS Authorization #</Label>
              <Input
                value={formData.paws_authorization_number}
                onChange={(e) => onChange('paws_authorization_number', e.target.value)}
                placeholder="Authorization number"
                className="font-mono"
                data-testid="dodd-paws"
              />
            </div>
            <div className="space-y-2">
              <Label>County BDD</Label>
              <Select value={formData.county_bdd || 'none'} onValueChange={(val) => onChange('county_bdd', val === 'none' ? '' : val)}>
                <SelectTrigger data-testid="dodd-county">
                  <SelectValue placeholder="Select county" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select county</SelectItem>
                  {OHIO_COUNTIES.map((county) => (
                    <SelectItem key={county} value={county}>{county}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea value={formData.notes} onChange={(e) => onChange('notes', e.target.value)} placeholder="Additional notes..." rows={2} data-testid="dodd-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-indigo-900 hover:bg-indigo-800" disabled={saving} data-testid="save-dodd-btn">
              {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Create Claim'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { WAIVER_TYPES, OHIO_COUNTIES };
